import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaPlus,
  FaTimesCircle,
  FaTools,
  FaFileAlt,
  FaSortAmountDown,
  FaSortAmountUp,
} from "react-icons/fa";
import { getAllDocsSnap } from "../../../utils/firebase/Firebase.utils";
import MobileWrapper from "../../../components/MobileWrapper";
import WorkPermitCard from "./WorkPermitCard";
import PageModal from "../Components/PageModal";
import WorkPermitDetails from "../WorkPermits/WorkPermitDetails";

function WorkPermitsAdminMobile() {
  const navigate = useNavigate();
  const [workPermits, setWorkPermits] = useState([]);
  const [filteredPermits, setFilteredPermits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilter, setSearchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [viewMode, setViewMode] = useState(false);

  // Color scheme
  const eleghoBlue = "#363062";
  const eleghoGreen = "#8FCD00";

  useEffect(() => {
    const fetchWorkPermits = async () => {
      await getAllDocsSnap("workPermits", (docs) => {
        // Sort by creation date
        docs.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA; // Newest first by default
        });
        setWorkPermits(docs);
        setFilteredPermits(docs);
        setIsLoading(false);
      });
    };
    fetchWorkPermits();
  }, []);

  useEffect(() => {
    if (workPermits.length > 0) {
      filterPermits();
    }
  }, [searchTerm, searchFilter, statusFilter, sortOrder, workPermits]);

  const filterPermits = () => {
    let filtered = [...workPermits];

    // Apply status filter first
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (permit) =>
          (permit.status || "").toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Then apply search filter (case insensitive)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((permit) => {
        // Case insensitive search by converting all values to lowercase
        switch (searchFilter) {
          case "id":
            return (permit.id || "").toLowerCase().includes(term);
          case "name":
            return (permit.name || "").toLowerCase().includes(term);
          case "unit":
            return (permit.identifier || "").toLowerCase().includes(term);
          case "type":
            return (permit.workPermitType || "").toLowerCase().includes(term);
          case "status":
            return (permit.status || "").toLowerCase().includes(term);
          case "all":
          default:
            // Search across all fields (case insensitive)
            return (
              (permit.id || "").toLowerCase().includes(term) ||
              (permit.name || "").toLowerCase().includes(term) ||
              (permit.identifier || "").toLowerCase().includes(term) ||
              (permit.workPermitType || "").toLowerCase().includes(term) ||
              (permit.status || "").toLowerCase().includes(term) ||
              (permit.description || "").toLowerCase().includes(term)
            );
        }
      });
    }

    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    setFilteredPermits(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "newest" ? "oldest" : "newest");
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status === statusFilter ? "all" : status);
  };

  const handleAddNew = () => {
    navigate("/admin/mobile/add-work-permit");
  };

  const handleCardClick = (permit) => {
    setSelectedPermit(permit);
    setViewMode(true);
  };

  // Count for each status type
  const getStatusCount = (status) => {
    if (status === "all") {
      // For the "all" status count, show total count
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase().trim();
        return workPermits.filter((permit) => {
          if (searchFilter === "all") {
            return (
              (permit.id || "").toLowerCase().includes(term) ||
              (permit.name || "").toLowerCase().includes(term) ||
              (permit.identifier || "").toLowerCase().includes(term) ||
              (permit.workPermitType || "").toLowerCase().includes(term) ||
              (permit.status || "").toLowerCase().includes(term) ||
              (permit.description || "").toLowerCase().includes(term)
            );
          } else {
            // Apply specific field filter
            switch (searchFilter) {
              case "id":
                return (permit.id || "").toLowerCase().includes(term);
              case "name":
                return (permit.name || "").toLowerCase().includes(term);
              case "unit":
                return (permit.identifier || "").toLowerCase().includes(term);
              case "type":
                return (permit.workPermitType || "")
                  .toLowerCase()
                  .includes(term);
              case "status":
                return (permit.status || "").toLowerCase().includes(term);
              default:
                return false;
            }
          }
        }).length;
      }
      return workPermits.length;
    }

    // For specific status counts
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      return workPermits.filter((permit) => {
        // First check if status matches
        if ((permit.status || "").toLowerCase() !== status.toLowerCase())
          return false;

        // Then apply search filters
        if (searchFilter === "all") {
          return (
            (permit.id || "").toLowerCase().includes(term) ||
            (permit.name || "").toLowerCase().includes(term) ||
            (permit.identifier || "").toLowerCase().includes(term) ||
            (permit.workPermitType || "").toLowerCase().includes(term) ||
            (permit.status || "").toLowerCase().includes(term) ||
            (permit.description || "").toLowerCase().includes(term)
          );
        } else {
          // Apply specific field filter
          switch (searchFilter) {
            case "id":
              return (permit.id || "").toLowerCase().includes(term);
            case "name":
              return (permit.name || "").toLowerCase().includes(term);
            case "unit":
              return (permit.identifier || "").toLowerCase().includes(term);
            case "type":
              return (permit.workPermitType || "").toLowerCase().includes(term);
            case "status":
              return (permit.status || "").toLowerCase().includes(term);
            default:
              return false;
          }
        }
      }).length;
    }

    // If no search term, just filter by status
    return workPermits.filter(
      (permit) => (permit.status || "").toLowerCase() === status.toLowerCase()
    ).length;
  };

  if (isLoading) {
    return (
      <MobileWrapper label="Work Permits">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
            <p className="mt-2 text-sm text-[#363062]">
              Loading work permits...
            </p>
          </div>
        </div>
      </MobileWrapper>
    );
  }

  return (
    <MobileWrapper label="Work Permits">
      <div className="w-full mx-auto px-4 max-w-4xl">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaFileAlt className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Work Permit Requests
              </h1>
              <p className="text-xs text-gray-500">
                Manage work permits for residents
              </p>
            </div>
          </div>
          <button
            className="flex items-center px-3 py-2 text-xs font-medium text-white rounded-md bg-[#8FCD00] shadow-sm hover:bg-[#7db700] transition-colors"
            onClick={handleAddNew}
          >
            <FaPlus className="w-3 h-3 mr-1.5" />
            New
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FaSearch className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
            placeholder="Search by ID, name, unit, or type..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ borderColor: "#E2E8F0" }}
          />
          {searchTerm && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setSearchTerm("")}
            >
              <FaTimesCircle className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Filter Controls */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
          <button
            onClick={toggleSortOrder}
            className="px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap border border-gray-200 bg-white text-[#363062] hover:bg-gray-50"
            title={sortOrder === "newest" ? "Newest first" : "Oldest first"}
          >
            {sortOrder === "newest" ? (
              <div className="flex items-center gap-1">
                <FaSortAmountDown className="w-3 h-3" />
                <span>Newest</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <FaSortAmountUp className="w-3 h-3" />
                <span>Oldest</span>
              </div>
            )}
          </button>
          <select
            className="px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap border border-gray-200 bg-white text-[#363062] hover:bg-gray-50"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          >
            <option value="all">All Fields</option>
            <option value="id">Permit ID</option>
            <option value="name">Name</option>
            <option value="unit">Unit</option>
            <option value="type">Type</option>
            <option value="status">Status</option>
          </select>
        </div>

        {/* Status Filter Buttons */}
        <div className="mb-4 overflow-x-auto">
          <div className="flex space-x-2 pb-2">
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                statusFilter === "all"
                  ? "bg-[#363062] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setStatusFilter("all")}
            >
              All ({getStatusCount("all")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                statusFilter === "pending"
                  ? "bg-[#FFA500] text-white"
                  : "bg-[#FFA500]/10 text-gray-700 hover:bg-[#FFA500]/20"
              }`}
              onClick={() => handleStatusFilter("pending")}
            >
              Pending ({getStatusCount("pending")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                statusFilter === "approved"
                  ? "bg-[#8FCD00] text-white"
                  : "bg-[#8FCD00]/10 text-gray-700 hover:bg-[#8FCD00]/20"
              }`}
              onClick={() => handleStatusFilter("approved")}
            >
              Approved ({getStatusCount("approved")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                statusFilter === "returned"
                  ? "bg-[#F44336] text-white"
                  : "bg-[#F44336]/10 text-gray-700 hover:bg-[#F44336]/20"
              }`}
              onClick={() => handleStatusFilter("returned")}
            >
              Returned ({getStatusCount("returned")})
            </button>
          </div>
        </div>

        {/* Work Permits List */}
        <div className="mt-4 space-y-3 pb-20">
          {filteredPermits.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FaFileAlt className="text-[#363062] text-lg" />
              </div>
              <h3 className="text-sm font-medium text-[#363062] mb-1">
                {filteredPermits.length === 0 && workPermits.length === 0
                  ? "No Work Permits"
                  : "No Matching Results"}
              </h3>
              <p className="text-xs text-gray-500">
                {filteredPermits.length === 0 && workPermits.length === 0
                  ? "No work permits have been submitted yet"
                  : "Try adjusting your filters or search terms"}
              </p>

              <button
                onClick={handleAddNew}
                className="mt-4 w-48 mx-auto bg-[#8FCD00] text-white py-2 rounded-lg flex items-center justify-center gap-2 text-xs font-medium"
              >
                <FaPlus className="w-3 h-3" />
                <span>Add New Work Permit</span>
              </button>
            </div>
          ) : (
            filteredPermits.map((permit) => (
              <WorkPermitCard
                key={permit.id}
                permit={permit}
                onClick={() => handleCardClick(permit)}
              />
            ))
          )}
        </div>
      </div>

      {viewMode && (
        <PageModal>
          <WorkPermitDetails
            application={selectedPermit}
            setViewMode={setViewMode}
          />
        </PageModal>
      )}
    </MobileWrapper>
  );
}

export default WorkPermitsAdminMobile;
